import { customInstance } from "@/api/client.ts"
import { getCustomer, getGetCustomerQueryKey } from "@/api/customer/customer.ts"
import {
  authSession,
  ensureFreshTokenIfAuthenticated,
} from "@/authentication/authenticationService"
import { onSessionCleared } from "@/authentication/useAuthSession"
import { nexusRouteFlags } from "@/lib/nexusRouteFlags.ts"
import envVariable from "@/lib/shared/envVariable.ts"
import shouldServeNexus from "@/middleware/shouldServeNexus.ts"
import ErrorReporter from "@/services/ErrorReporter"
import type { QueryClient } from "@tanstack/vue-query"
import { watch } from "vue"
import type { Router } from "vue-router"

/**
 * So Energy's chat widget (customer-support-center `chat-widget/`, ADR-0006), the site's chat on
 * every page but /myaccount/chat (the native app's chat page). It loads from
 * `VITE_SO_CHAT_WIDGET_LOADER`; unset, there is no site-wide chat. Each build of the loader is
 * pinned to its environment's widget API, so a Preview or staging build pointing at the dev CDN can
 * never open a chat on the production instance.
 *
 * Signed in, a chat is started by Portal V2 (`POST /api/v1/accounts/{accountId}/chat`), which
 * checks the session and account ownership and starts the chat authenticated, so the assistant
 * skips ID&V. Signed out, `startChat` resolves null and the widget starts an anonymous chat itself.
 */
export interface StartChatDetails {
  contactId: string
  participantId: string
  participantToken: string
}

/** What the widget shows in its status strip: a badge only when `verified`. */
export interface SoChatIdentity {
  verified: boolean
  name?: string
}

type SoChatFn = ((command: string, arg?: unknown) => void) & { q?: unknown[][] }
type SoChatWindow = Window & { SoChat?: SoChatFn }

/** The catch-all route in routes.web.ts; a link that resolves to it is not ours to handle. */
const NOT_FOUND_ROUTE = "notFound"

export const loaderUrl = (): string =>
  envVariable.get("VITE_SO_CHAT_WIDGET_LOADER")?.trim() ?? ""

export const isSoChatEnabled = (): boolean => loaderUrl() !== ""

/**
 * The widget's command queue, the same stub its embed snippet defines: calls made before the loader
 * arrives are queued on `SoChat.q` and replayed by the loader once it loads.
 */
export const soChat = (command: string, arg?: unknown): void => {
  const w = window as SoChatWindow
  if (!w.SoChat) {
    const stub: SoChatFn = (...args) => {
      stub.q ??= []
      stub.q.push(args)
    }
    w.SoChat = stub
  }
  w.SoChat(command, arg)
}

/**
 * A start Portal V2 refused. The widget reads `status` and `code` off the rejection: 401 with
 * `session_expired` shows its sign-in message, 429 its busy state, anything else a generic error.
 */
export class ChatStartRefused extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(code ?? `Chat start refused (${status})`)
    this.name = "ChatStartRefused"
  }
}

/** The signed-in customer, from the query cache when the app has already loaded it. */
const currentCustomer = (queryClient: QueryClient) =>
  queryClient.ensureQueryData({
    queryKey: getGetCustomerQueryKey(),
    queryFn: ({ signal }) => getCustomer(undefined, signal),
  })

/** The signed-in customer's current account, as an opaque key; undefined without one. */
export const currentAccountKey = async (
  queryClient: QueryClient,
): Promise<string | undefined> => {
  const { accountIDLastVisited } = await currentCustomer(queryClient)
  return accountIDLastVisited ? String(accountIDLastVisited) : undefined
}

/**
 * Starts a signed-in chat through Portal V2 for the account the customer is looking at right now,
 * so a switch of account is honoured. Only for a caller that holds a session: the website's own
 * widget, and the native app on behalf of its chat page (src/lib/soChatAppBridge.ts).
 */
export const startSignedInChat = async (
  queryClient: QueryClient,
): Promise<StartChatDetails> => {
  try {
    const { accountIDLastVisited: accountId } =
      await currentCustomer(queryClient)
    if (!accountId) throw new Error("Signed in with no current account")
    return await customInstance<StartChatDetails>({
      url: `/api/v1/accounts/${accountId}/chat`,
      method: "POST",
      // A 403 here is chat being switched off for this customer, not a lost session: the widget
      // shows its own error, so the app's "please log in again" dialog stays out of it.
      skipAuthErrorDialog: true,
    })
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status
    if (status === 401) throw new ChatStartRefused(401, "session_expired")
    if (status === 429) throw new ChatStartRefused(429)
    throw error
  }
}

/**
 * Called by the widget on every press of Start. Signed out: null (anonymous chat). Signed in: a
 * chat started through Portal V2.
 */
export const startChatWith =
  (queryClient: QueryClient) => async (): Promise<StartChatDetails | null> => {
    await ensureFreshTokenIfAuthenticated()
    if (!authSession.isAuthenticated.value) return null
    return startSignedInChat(queryClient)
  }

/** Same-origin links in replies open in the app, without a page load, when the app serves them. */
export const navigateWith =
  (router: Router) =>
  (url: string): boolean => {
    let target: URL
    try {
      target = new URL(url, window.location.href)
    } catch {
      return false
    }
    if (target.origin !== window.location.origin) return false
    if (!shouldServeNexus(target.pathname, nexusRouteFlags)) return false
    const resolved = router.resolve(
      target.pathname + target.search + target.hash,
    )
    if (!resolved.matched.length || resolved.name === NOT_FOUND_ROUTE)
      return false
    void router.push(resolved.fullPath)
    return true
  }

export const identityOf = async (
  queryClient: QueryClient,
): Promise<SoChatIdentity> => {
  try {
    const name = (await currentCustomer(queryClient)).firstName?.trim()
    return name ? { verified: true, name } : { verified: true }
  } catch {
    return { verified: true }
  }
}

/** Adds the loader script to the page, after `SoChat('init')` has been queued. Once per page. */
export const loadSoChatLoader = (): void => {
  const script = document.createElement("script")
  script.src = loaderUrl()
  script.async = true
  script.onerror = () => {
    ErrorReporter.report(
      new Error("So Energy chat widget loader failed to load"),
      {
        src: script.src,
      },
    )
    // The same event the widget sends when its panel fails, so a page has one failure to watch.
    window.dispatchEvent(
      new CustomEvent("so-chat:error", {
        detail: { reason: "loader_load_failed" },
      }),
    )
  }
  document.head.appendChild(script)
}

/**
 * Loads the widget once and keeps its identity in step with the session.
 *
 * The session is resolved before the widget is told anything. On a reload the access token lives
 * in memory only, so the app starts signed out until the refresh cookie is used; the widget, on
 * the other hand, resumes a stored chat as soon as its panel mounts, and resumes a signed-in chat
 * only if the host says the customer is signed in at that moment. Initialising first would lose a
 * signed-in customer's chat on every reload. A visitor with no refresh cookie waits for nothing.
 *
 * Resolves to a function that stops following the session (the app never calls it; tests do).
 */
export const initSoChat = async (
  router: Router,
  queryClient: QueryClient,
  /** Extra init options for the widget, such as `{ launcher: false }` for a whole-page chat. */
  options: Record<string, unknown> = {},
): Promise<() => void> => {
  await ensureFreshTokenIfAuthenticated()

  // Whether the widget has been told the customer is signed in. Only a sign-in sets it, and only a
  // cleared session after one ends the chat, so a visitor's anonymous chat survives a session clear
  // that was never theirs (a failed auto-login, say). A lapsed access token is not a sign-out: it is
  // refreshed on the next request, so it neither clears the badge nor ends the chat.
  let signedIn = authSession.isAuthenticated.value
  const identity = signedIn ? await identityOf(queryClient) : undefined

  soChat("init", {
    ...options,
    startChat: startChatWith(queryClient),
    navigate: navigateWith(router),
    ...(identity && { identity }),
  })

  const stopWatching = watch(
    () => authSession.isAuthenticated.value,
    async (isAuthenticated) => {
      if (!isAuthenticated) return
      const identity = await identityOf(queryClient)
      // Signed out again while the name was loading: the clear below has already run.
      if (!authSession.isAuthenticated.value) return
      signedIn = true
      soChat("update", { identity })
    },
  )

  // Log out, a failed refresh and the auto-login guard all clear the session: the widget forgets
  // the customer, and a live signed-in chat is ended so the next person on this device cannot see it.
  const stopListening = onSessionCleared(() => {
    // The cached customer belongs to the session that was just cleared. Only the logout button
    // empties the cache; after any other clear, the next sign-in must not reuse that record for the
    // badge or for Start, so it is dropped here whatever happened before.
    queryClient.removeQueries({ queryKey: getGetCustomerQueryKey() })
    if (!signedIn) return
    signedIn = false
    soChat("update", { identity: { verified: false } })
    soChat("end")
  })

  loadSoChatLoader()

  return () => {
    stopWatching()
    stopListening()
  }
}
